/**
 * Phase 9.15b: EditContactModal
 * Modal to edit existing contact from invoice detail page
 * Pre-fills form with current contact data
 */
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useUpdateOrganizationContact } from '@/hooks';

const editContactSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  title: z.string().optional(),
  is_primary: z.boolean().default(false),
});

type EditContactFormData = z.infer<typeof editContactSchema>;

interface Contact {
  id: string;
  organization_id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  is_primary_contact?: boolean;
}

interface EditContactModalProps {
  open: boolean;
  contact: Contact;
  onOpenChange: (open: boolean) => void;
  onContactUpdated: () => void;
}

export function EditContactModal({
  open,
  contact,
  onOpenChange,
  onContactUpdated,
}: EditContactModalProps) {
  const { control, handleSubmit, formState: { errors }, reset } = useForm<EditContactFormData>({
    resolver: zodResolver(editContactSchema),
    defaultValues: {
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      is_primary: contact.is_primary_contact || false,
    },
  });

  const updateContact = useUpdateOrganizationContact();

  // Reset form when contact changes or modal reopens
  useEffect(() => {
    if (open) {
      reset({
        name: contact.name,
        email: contact.email || '',
        phone: contact.phone || '',
        title: contact.title || '',
        is_primary: contact.is_primary_contact || false,
      });
    }
  }, [open, contact, reset]);

  const onSubmit = async (data: EditContactFormData) => {
    try {
      await updateContact.mutateAsync({
        organizationId: contact.organization_id,
        contactId: contact.id,
        data: {
          name: data.name,
          email: data.email || undefined,
          phone: data.phone || undefined,
          title: data.title || undefined,
          is_primary_contact: data.is_primary,
        },
      });
      onContactUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update contact:', error);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Contact</DialogTitle>
          <DialogDescription>
            Update contact information for {contact.name}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="edit-name">Name *</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="edit-name"
                  placeholder="Enter contact name"
                  {...field}
                />
              )}
            />
            {errors.name && (
              <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <Label htmlFor="edit-email">
              Email <span className="text-gray-500 text-sm">(Optional)</span>
            </Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Input
                  id="edit-email"
                  type="email"
                  placeholder="contact@example.com"
                  {...field}
                />
              )}
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label htmlFor="edit-phone">
              Phone <span className="text-gray-500 text-sm">(Optional)</span>
            </Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input
                  id="edit-phone"
                  type="tel"
                  placeholder="555-1234"
                  {...field}
                />
              )}
            />
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="edit-title">
              Title <span className="text-gray-500 text-sm">(Optional)</span>
            </Label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  id="edit-title"
                  placeholder="e.g., Project Manager"
                  {...field}
                />
              )}
            />
          </div>

          {/* Primary Contact */}
          <div className="flex items-center space-x-2">
            <Controller
              name="is_primary"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="edit-is_primary"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label
              htmlFor="edit-is_primary"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Set as primary contact
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateContact.isPending}>
              {updateContact.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
